/* ============================================================
   bundle-builder.js
   Dansu Bundle Builder — custom element + pricing engine
   Relies on global scope: trapFocus(), removeTrapFocus() from global.js
   ============================================================ */

'use strict';

/* ============================================================
   A) BundlePricing — pure pricing calculations
   ============================================================ */
const BundlePricing = (() => {
  /** Unit prices in pence */
  const UNIT_PRICES = {
    towel: 2995,
    clip: 395,
    bottle: 2000,
    electrolytes: 1195,
  };

  /**
   * Towel-only bundle prices in pence.
   * Key = number of towels selected.
   */
  const TOWEL_QTY_PRICES = {
    1: 2995,   // £29.95 (full price)
    2: 4995,   // £49.95 (fixed bundle price)
    3: 7495,   // £74.95 (fixed bundle price)
    4: 9995,   // £99.95 (fixed bundle price)
  };

  /**
   * Discount codes keyed by tier percentage.
   */
  const DISCOUNT_CODES = {
    10: 'BUNDLE10',
    15: 'BUNDLE15',
    20: 'BUNDLE20',
    25: 'BUNDLE25',
  };

  /**
   * Fixed-amount discount codes for towel-only bundles.
   */
  const TOWEL_DISCOUNT_CODES = {
    2: 'TOWEL2',
    3: 'TOWEL3',
    4: 'TOWEL4',
  };

  /**
   * Rave-pack discount as a PERCENTAGE (16.6% ≈ the historic flat GBP deal:
   * DUO £49.95, CREW £74.95). A percentage converts across every market currency
   * for both the displayed price and the Shopify checkout discount.
   * Backed by the RAVEPACK16 percentage discount code. Keep in sync with
   * pack_discount_pct in snippets/pdp-rave-pack.liquid.
   */
  const RAVE_PACK_PCT = 16.6;
  const RAVE_PACK_CODE = 'RAVEPACK16';

  /**
   * Return discount tier based on item count.
   * @param {number} totalItems
   * @returns {number} 0 | 10 | 15 | 20 | 25
   */
  function getDiscountTier(totalItems) {
    if (totalItems < 2) return 0;
    if (totalItems < 3) return 10;
    if (totalItems < 6) return 15;
    if (totalItems < 10) return 20;
    return 25;
  }

  /**
   * Get the towel-only bundle price for a given quantity.
   * @param {number} qty
   * @returns {number} price in pence
   */
  function getTowelOnlyPrice(qty) {
    if (qty <= 0) return 0;
    if (TOWEL_QTY_PRICES[qty]) return TOWEL_QTY_PRICES[qty];
    return qty * UNIT_PRICES.towel;
  }

  /**
   * Format minor units as currency string using Shopify's active currency.
   * Falls back to £ if Shopify.currency is not available.
   * @param {number} minorUnits - price in minor units (pence/cents)
   * @returns {string}
   */
  function formatPrice(minorUnits) {
    const major = minorUnits / 100;
    try {
      if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: Shopify.currency.active,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(major);
      }
    } catch (e) { /* fallback below */ }
    return '£' + major.toFixed(2);
  }

  /**
   * Count distinct product types present in selections.
   * @param {object} selections
   * @returns {number}
   */
  function countProductTypes(selections) {
    let types = 0;
    if (selections.towels && selections.towels.length > 0) types++;
    if (selections.clipQty > 0) types++;
    if (selections.bottle > 0) types++;
    if (selections.electrolytes > 0) types++;
    return types;
  }

  /**
   * Main pricing calculation.
   *
   * @param {object} selections - { towels: Array, clipQty: number, bottle: number }
   * @returns {object}
   */
  function calculate(selections) {
    const towelCount       = (selections.towels || []).length;
    const clipCount        = selections.clipQty || 0;
    const bottleCount      = selections.bottle || 0;
    const electrolytesCount = selections.electrolytes || 0;

    const totalItems = towelCount + clipCount + bottleCount + electrolytesCount;
    const productTypes = countProductTypes(selections);
    const isTowelOnly = productTypes === 1 && towelCount > 0;

    const fullRRP = (towelCount * UNIT_PRICES.towel)
      + (clipCount * UNIT_PRICES.clip)
      + (bottleCount * UNIT_PRICES.bottle)
      + (electrolytesCount * UNIT_PRICES.electrolytes);

    let bundlePrice;
    let discountPercent;
    let discountCode = null;

    if (isTowelOnly && towelCount >= 2) {
      // Towel-only pack: flat percentage off the (already market-converted) RRP,
      // so price + saving + the checkout discount are correct in EVERY currency.
      discountPercent = RAVE_PACK_PCT;
      bundlePrice = Math.round(fullRRP * (1 - discountPercent / 100));
      discountCode = RAVE_PACK_CODE;
    } else if (totalItems >= 2) {
      // 2+ items: item-count tier ONLY — the BUNDLE codes enforce count minimums
      // (2/3/6/10) at checkout, so display must never promise more than count earns.
      discountPercent = getDiscountTier(totalItems);
      discountCode = discountPercent > 0 ? DISCOUNT_CODES[discountPercent] : null;
      bundlePrice = Math.round(fullRRP * (1 - discountPercent / 100));
    } else {
      bundlePrice = fullRRP;
      discountPercent = 0;
    }

    const saving = fullRRP - bundlePrice;

    return {
      fullRRP,
      bundlePrice,
      saving,
      discountPercent,
      discountCode,
      isTowelOnly,
      totalItems,
      towelCount,
      clipCount,
      bottleCount,
      electrolytesCount,
      productTypes,
    };
  }

  /**
   * Nudge for next discount tier.
   * Only shows when the next tier is 1-2 items away.
   * @param {object} pricing - output of calculate()
   * @returns {string|null}
   */
  function getNextTierNudge(pricing) {
    const { totalItems, discountPercent } = pricing;
    if (totalItems === 0) return null;

    const tiers = [
      { min: 2,  pct: 10 },
      { min: 3,  pct: 15 },
      { min: 6,  pct: 20 },
      { min: 10, pct: 25 },
    ];

    const next = tiers.find(t => totalItems < t.min);
    if (!next) return null;

    const needed = next.min - totalItems;
    // Only nudge when close to next tier (1-2 items away)
    if (needed > 2) return null;

    return `Add ${needed} more item${needed !== 1 ? 's' : ''} to save up to ${next.pct}%`;
  }

  return {
    UNIT_PRICES,
    TOWEL_QTY_PRICES,
    DISCOUNT_CODES,
    RAVE_PACK_CODE,
    calculate,
    getDiscountTier,
    formatPrice,
    getNextTierNudge,
  };
})();

/* ============================================================
   B) BundleBuilder — custom element
   ============================================================ */
class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this._sectionId = this.dataset.section;
    this._state     = this._emptyState();
    this._products  = null;
    this._activeImageIndex = 0;
  }

  /* ---- Lifecycle ---- */

  connectedCallback() {
    this._parseProductData();
    this._populateBottleCardImg();
    this._queryElements();
    this._bindEvents();
    this._loadSavedState();
    this._updateUI();

    // Handle URL params — reset state first so back/forward doesn't double-add
    const params = new URLSearchParams(window.location.search);
    const hasUrlParams = params.get('preset') || params.get('add') || params.get('towels');
    if (hasUrlParams) {
      this.initState();
    }

    const urlPreset = params.get('preset');
    if (urlPreset) {
      this._loadPreset(urlPreset);
      this._updateUI();
    }

    const addHandle = params.get('add');
    if (addHandle) {
      this._addByHandle(addHandle);
      this._updateUI();
    }

    const towelHandles = params.get('towels');
    if (towelHandles) {
      towelHandles.split(',').forEach(h => this._addByHandle(h.trim()));
      this._updateUI();
    }

    const bottleQty = params.get('bottle');
    if (bottleQty) { this.setBottle(parseInt(bottleQty, 10)); }
    const clipQty = params.get('clip');
    if (clipQty) { this.setClip(parseInt(clipQty, 10)); }
    const electrolytesQty = params.get('electrolytes');
    if (electrolytesQty) { this.setElectrolytes(parseInt(electrolytesQty, 10)); }
  }

  /* ---- State ---- */

  _emptyState() {
    return {
      towels: [],       // Array<{ variantId, title, image, swatch }>
      clipQty: 0,
      bottle: 0,
      electrolytes: 0,
    };
  }

  initState() {
    this._state = this._emptyState();
    this._activeImageIndex = 0;
  }

  /* ---- Product data ---- */

  _parseProductData() {
    const scriptEl = document.getElementById(`bundle-product-data-${this._sectionId}`);
    if (!scriptEl) {
      console.warn('[BundleBuilder] Product data script element not found.');
      return;
    }
    try {
      this._products = JSON.parse(scriptEl.textContent);
    } catch (e) {
      console.error('[BundleBuilder] Failed to parse product data:', e);
      return;
    }

    // Sync unit prices with actual Shopify product data so pricing always matches
    if (this._products) {
      if (this._products.towels && this._products.towels[0]) {
        BundlePricing.UNIT_PRICES.towel = this._products.towels[0].price;
      }
      if (this._products.clip && this._products.clip.variants && this._products.clip.variants[0]) {
        BundlePricing.UNIT_PRICES.clip = this._products.clip.variants[0].price;
      }
      if (this._products.bottle) {
        BundlePricing.UNIT_PRICES.bottle = this._products.bottle.price;
      }
      if (this._products.electrolytes) {
        BundlePricing.UNIT_PRICES.electrolytes = this._products.electrolytes.price;
      }
    }
  }

  _populateBottleCardImg() {
    const img = document.getElementById(`BottleCardImg-${this._sectionId}`);
    if (!img) return;
    const src = this._products && this._products.bottle && this._products.bottle.image;
    if (src) {
      img.src = src;
      img.style.display = '';
    }
  }

  /* ---- DOM queries ---- */

  _queryElements() {
    const sid = this._sectionId;
    this._priceNow      = document.getElementById(`BundlePriceNow-${sid}`);
    this._priceWas      = document.getElementById(`BundlePriceWas-${sid}`);
    this._priceSaving   = document.getElementById(`BundlePriceSaving-${sid}`);
    this._addToCart     = document.getElementById(`BundleAddToCart-${sid}`);
    this._selectedTowels = document.getElementById(`SelectedTowels-${sid}`);
    this._selectedClips  = document.getElementById(`SelectedClips-${sid}`);
    this._summaryItems  = document.getElementById(`BundleSummaryItems-${sid}`);
    this._liveRegion    = document.getElementById(`BundleLive-${sid}`);
    this._priceNudge    = document.getElementById(`BundlePriceNudge-${sid}`);
    this._mobileDrawer      = document.getElementById(`BundleMobileDrawer-${sid}`);
    this._mobileDrawerItems = document.getElementById(`BundleDrawerItems-${sid}`);
    this._mobileDrawerCount = document.getElementById(`BundleMobileCount-${sid}`);
    this._towelGrid         = document.getElementById(`TowelSwatchGrid-${sid}`);
    this._expressCheckout   = document.getElementById(`BundleExpressCheckout-${sid}`);
    this._desktopSummaryCount = document.getElementById(`BundleDesktopCount-${sid}`);
    this._desktopClearWrap    = document.getElementById(`BundleDesktopClear-${sid}`);
    this._summaryPanel        = document.getElementById(`BundleRight-${sid}`);
  }

  /* ---- Event binding ---- */

  _bindEvents() {
    // Add-to-cart
    if (this._addToCart) {
      this._addToCart.addEventListener('click', () => this._handleAddToCart());
    }

    // Delegated handlers on the element itself
    this.addEventListener('click', (e) => this._handleDelegatedClick(e));

    // Preset card clicks (they use data-action="open-bundle-builder" with data-preset)
    this.querySelectorAll('[data-preset]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const presetId = btn.dataset.preset;
        if (presetId) {
          this.initState();
          this._loadPreset(presetId);
          this._updateUI();
          // Scroll to builder
          const body = this.querySelector('.bb-body');
          if (body) body.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ---- Delegated click router ---- */

  _handleDelegatedClick(e) {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;

    const action   = btn.dataset.action;
    const variantId = btn.dataset.variantId ? parseInt(btn.dataset.variantId, 10) : null;
    const qty      = btn.dataset.qty !== undefined ? parseInt(btn.dataset.qty, 10) : null;
    const index    = btn.dataset.index !== undefined ? parseInt(btn.dataset.index, 10) : null;

    switch (action) {
      case 'add-towel':
        if (variantId) {
          this.addTowel(variantId, btn.dataset.title, btn.dataset.image, btn.dataset.swatch, btn.dataset.handle);
        }
        break;
      case 'remove-towel':
        if (index !== null) this.removeTowel(index);
        break;
      case 'set-clip':
        if (qty !== null) this.setClip(qty);
        break;
      case 'set-bottle':
        if (qty !== null) this.setBottle(qty);
        break;
      case 'set-electrolytes':
        if (qty !== null) this.setElectrolytes(qty);
        break;
      case 'increment-clip':
        this.setClip(this._state.clipQty + 1);
        break;
      case 'decrement-clip':
        this.setClip(this._state.clipQty - 1);
        break;
      case 'increment-bottle':
        this.setBottle(this._state.bottle + 1);
        break;
      case 'decrement-bottle':
        this.setBottle(this._state.bottle - 1);
        break;
      case 'buy-now':
        this._handleBuyNow();
        break;
      case 'clear-bundle':
        this.initState();
        try { localStorage.removeItem('dansu_bundle_state'); } catch (e) {}
        this._updateUI();
        this._announce('Bundle cleared.');
        break;
      case 'clear-cart':
        this._handleClearCart();
        break;
      case 'show-image':
        if (index !== null) this._showImageAtIndex(index);
        break;
      case 'toggle-drawer':
        this._toggleMobileDrawer();
        break;
      case 'toggle-desktop-summary':
        this._toggleDesktopSummary();
        break;
      case 'toggle-towels':
        this._toggleTowels(btn);
        break;
    }
  }

  /* ---- See-all towel grid toggle ---- */

  _toggleTowels(btn) {
    if (!this._towelGrid) return;
    const collapsed = this._towelGrid.classList.toggle('bb-swatch-grid--collapsed');
    const expanded = !collapsed;
    btn.setAttribute('aria-expanded', String(expanded));
    btn.classList.toggle('bb-see-all--expanded', expanded);
    const label = btn.querySelector('.bb-see-all__label');
    if (label) {
      label.textContent = expanded
        ? (btn.dataset.labelLess || 'Show less')
        : (btn.dataset.labelMore || 'See all');
    }
  }

  /* ---- State mutations ---- */

  addTowel(variantId, title, image, swatch, handle) {
    /* No towel limit — customers can add as many as they want */
    this._state.towels = [...this._state.towels, { variantId, title, image, swatch, handle }];
    this._announce(`${title} added.`);
    this._updateUI();
  }

  removeTowel(index) {
    const removed = this._state.towels[index];
    this._state.towels = this._state.towels.filter((_, i) => i !== index);
    if (removed) this._announce(`${removed.title} removed.`);
    this._activeImageIndex = 0;
    this._updateUI();
  }

  setClip(qty) {
    this._state.clipQty = Math.max(0, Math.min(4, qty));
    this._updateUI();
  }

  setBottle(qty) {
    this._state.bottle = Math.max(0, Math.min(4, qty));
    this._updateUI();
  }

  setElectrolytes(qty) {
    this._state.electrolytes = Math.max(0, Math.min(4, qty));
    this._updateUI();
  }

  /* ---- Pricing accessor ---- */

  getCurrentPricing() {
    return BundlePricing.calculate(this._state);
  }

  /* ---- Preset loading ---- */

  _loadPreset(presetId) {
    if (!this._products) return;

    const firstTowel = this._products.towels ? this._products.towels.find(t => t.available) : null;
    const firstClip = this._products.clip && this._products.clip.variants
      ? this._products.clip.variants.find(v => v.available)
      : null;

    switch (presetId) {
      case 'starter-pack':
        if (firstTowel) {
          this._state.towels.push({
            variantId: firstTowel.variantId, title: firstTowel.title,
            image: firstTowel.image, swatch: firstTowel.swatch,
          });
        }
        break;

      case 'rave-pack':
        if (firstTowel) {
          this._state.towels.push({
            variantId: firstTowel.variantId, title: firstTowel.title,
            image: firstTowel.image, swatch: firstTowel.swatch,
          });
        }
        this._state.clipQty = 1;
        this._state.bottle = 1;
        break;

      case 'towel-duo':
        if (firstTowel) {
          this._state.towels.push({
            variantId: firstTowel.variantId, title: firstTowel.title,
            image: firstTowel.image, swatch: firstTowel.swatch,
          });
          this._state.towels.push({
            variantId: firstTowel.variantId, title: firstTowel.title,
            image: firstTowel.image, swatch: firstTowel.swatch,
          });
        }
        break;

      case 'rave-duo':
        if (firstTowel) {
          this._state.towels.push({
            variantId: firstTowel.variantId, title: firstTowel.title,
            image: firstTowel.image, swatch: firstTowel.swatch,
          });
          this._state.towels.push({
            variantId: firstTowel.variantId, title: firstTowel.title,
            image: firstTowel.image, swatch: firstTowel.swatch,
          });
        }
        this._state.clipQty = 2;
        this._state.bottle = 2;
        break;
    }
  }

  /* ---- Add product by handle (from URL param) ---- */

  _addByHandle(handle) {
    if (!this._products) return;

    // Check towels
    if (this._products.towels) {
      const towel = this._products.towels.find(t => t.handle === handle);
      if (towel && towel.available !== false) {
        this._state.towels.push({
          variantId: towel.variantId,
          title: towel.title,
          image: towel.image,
          swatch: towel.swatch,
          handle: towel.handle,
        });
        return;
      }
    }

    // Check bottle
    if (handle === 'insulated-water-bottle' && this._products.bottle && this._products.bottle.available !== false) {
      this._state.bottle = Math.min(4, this._state.bottle + 1);
      return;
    }

    // Check clip
    if (handle === 'xl-clip' && this._products.clip) {
      this._state.clipQty = Math.min(4, this._state.clipQty + 1);
      return;
    }

    // Check electrolytes
    if (handle === 'electrolyte-sachets' && this._products.electrolytes && this._products.electrolytes.available !== false) {
      this._state.electrolytes = Math.min(4, this._state.electrolytes + 1);
      return;
    }
  }

  /* ---- UI update orchestrator ---- */

  _saveState() {
    try {
      localStorage.setItem('dansu_bundle_state', JSON.stringify(this._state));
    } catch (e) {}
  }

  _loadSavedState() {
    try {
      const saved = localStorage.getItem('dansu_bundle_state');
      if (saved) {
        const parsed = JSON.parse(saved);
        this._state = { ...this._emptyState(), ...parsed };
      }
    } catch (e) {}
  }

  _updateUI() {
    this._saveState();
    const pricing = this.getCurrentPricing();
    this._renderPriceBar(pricing);
    this._renderSelectedTowels();
    this._renderSelectedClips();
    this._renderSummaryList();
    this._renderMobileDrawer();
    this._updateButtonState(pricing);
    this._updateQtyDisplays();
    this._updateTowelSwatchSelectedStates();

    /* Mobile auto-scroll removed — users found it disorienting */
  }

  /* ---- Render: Price bar ---- */

  _renderPriceBar(pricing) {
    const { fullRRP, bundlePrice, saving, discountPercent, totalItems } = pricing;

    if (!this._priceNow) return;

    this._priceNow.textContent = BundlePricing.formatPrice(bundlePrice);

    if (saving > 0 && this._priceWas) {
      this._priceWas.textContent = BundlePricing.formatPrice(fullRRP);
    } else if (this._priceWas) {
      this._priceWas.textContent = '';
    }

    if (this._priceSaving) {
      if (saving > 0) {
        this._priceSaving.textContent = `You're saving ${BundlePricing.formatPrice(saving)}`;
        this._priceSaving.classList.remove('bb-price-bar__saving--muted');
      } else {
        this._priceSaving.textContent = '';
      }
    }

    if (this._priceNudge) {
      const nudge = BundlePricing.getNextTierNudge(pricing);
      if (nudge) {
        this._priceNudge.textContent = nudge;
      } else {
        this._priceNudge.textContent = '';
      }
    }
  }

  /* ---- Render: Summary list (right panel) ---- */

  _renderSummaryList() {
    if (!this._summaryItems) return;

    const items = [];

    this._state.towels.forEach((towel, i) => {
      const imgHtml = towel.swatch
        ? `<img class="bb-summary-item__swatch" src="${towel.swatch}" alt="" width="28" height="28">`
        : '';
      const linkHtml = towel.handle
        ? `<a class="bb-summary-item__link" href="/products/${this._escHtml(towel.handle)}">See product</a>`
        : '';
      items.push(`
        <div class="bb-summary-item">
          ${imgHtml}
          <div class="bb-summary-item__details">
            <span class="bb-summary-item__name">${this._escHtml(towel.title)}</span>
            ${linkHtml}
          </div>
          <button class="bb-summary-item__remove" type="button" data-action="remove-towel" data-index="${i}" aria-label="Remove ${this._escHtml(towel.title)}">&times;</button>
        </div>
      `);
    });

    if (this._state.clipQty > 0) {
      const clipImg = this._products && this._products.clip && this._products.clip.image
        ? `<img class="bb-summary-item__swatch" src="${this._products.clip.image}" alt="" width="28" height="28">`
        : '';
      items.push(`
        <div class="bb-summary-item">
          ${clipImg}
          <div class="bb-summary-item__details">
            <span class="bb-summary-item__name">XL Clip</span>
            <a class="bb-summary-item__link" href="/products/xl-clip">See product</a>
          </div>
          <span class="bb-summary-item__qty">&times;${this._state.clipQty}</span>
          <button class="bb-summary-item__remove" type="button" data-action="set-clip" data-qty="0" aria-label="Remove clips">&times;</button>
        </div>
      `);
    }

    if (this._state.bottle > 0) {
      const bottleImg = this._products && this._products.bottle && this._products.bottle.image
        ? `<img class="bb-summary-item__swatch" src="${this._products.bottle.image}" alt="" width="28" height="28">`
        : '';
      items.push(`
        <div class="bb-summary-item">
          ${bottleImg}
          <div class="bb-summary-item__details">
            <span class="bb-summary-item__name">Water Bottle</span>
            <a class="bb-summary-item__link" href="/products/insulated-water-bottle">See product</a>
          </div>
          <span class="bb-summary-item__qty">&times;${this._state.bottle}</span>
          <button class="bb-summary-item__remove" type="button" data-action="set-bottle" data-qty="0" aria-label="Remove bottle">&times;</button>
        </div>
      `);
    }

    if (this._state.electrolytes > 0) {
      const electrolytesImg = this._products && this._products.electrolytes && this._products.electrolytes.image
        ? `<img class="bb-summary-item__swatch" src="${this._products.electrolytes.image}" alt="" width="28" height="28">`
        : '';
      items.push(`
        <div class="bb-summary-item">
          ${electrolytesImg}
          <div class="bb-summary-item__details">
            <span class="bb-summary-item__name">Reload Electrolytes</span>
            <a class="bb-summary-item__link" href="/products/electrolyte-sachets">See product</a>
          </div>
          <span class="bb-summary-item__qty">&times;${this._state.electrolytes}</span>
          <button class="bb-summary-item__remove" type="button" data-action="set-electrolytes" data-qty="0" aria-label="Remove electrolytes">&times;</button>
        </div>
      `);
    }

    if (items.length === 0) {
      this._summaryItems.innerHTML = '<p class="bb-summary__empty">Select items to build your bundle</p>';
    } else {
      this._summaryItems.innerHTML = items.join('');
    }

    // Update desktop summary count badge
    const pricing = this.getCurrentPricing();
    if (this._desktopSummaryCount) {
      this._desktopSummaryCount.textContent = pricing.totalItems;
    }

    // Render clear button inside desktop collapsible
    if (this._desktopClearWrap) {
      if (items.length > 0) {
        this._desktopClearWrap.innerHTML = '<button class="bb-clear-bundle bb-clear-bundle--drawer" type="button" data-action="clear-bundle">Clear Bundle</button>';
      } else {
        this._desktopClearWrap.innerHTML = '';
      }
    }
  }

  /* ---- Render: Selected towel chips ---- */

  _renderSelectedTowels() {
    if (!this._selectedTowels) return;

    if (this._state.towels.length === 0) {
      this._selectedTowels.innerHTML = '';
      return;
    }

    const html = this._state.towels.map((towel, i) => {
      const imgHtml = towel.swatch
        ? `<img class="bb-chip__img" src="${towel.swatch}" alt="" width="20" height="20" loading="lazy">`
        : '';
      return `
        <span class="bb-chip">
          ${imgHtml}
          <span class="bb-chip__label">${this._escHtml(towel.title)}</span>
          <button
            class="bb-chip__remove"
            type="button"
            data-action="remove-towel"
            data-index="${i}"
            aria-label="Remove ${this._escHtml(towel.title)}"
          >&times;</button>
        </span>
      `;
    }).join('');

    this._selectedTowels.innerHTML = html;
  }

  /* ---- Render: Selected clip chips ---- */

  _renderSelectedClips() {
    if (!this._selectedClips) return;

    if (this._state.clipQty === 0) {
      this._selectedClips.innerHTML = '';
      return;
    }

    this._selectedClips.innerHTML = `
      <span class="bb-chip">
        <span class="bb-chip__color" style="background:#1a1a1a;"></span>
        <span class="bb-chip__label">Clip &times;${this._state.clipQty}</span>
        <button
          class="bb-chip__remove"
          type="button"
          data-action="set-clip"
          data-qty="0"
          aria-label="Remove clips"
        >&times;</button>
      </span>
    `;
  }

  /* ---- Render: Product image ---- */

  _renderProductImage() {
    if (!this._imageEl || !this._imageEmpty) return;

    const allImages = this._getAllImages();

    if (allImages.length === 0) {
      this._imageEl.classList.remove('bb-is-visible');
      this._imageEmpty.style.display = '';
      return;
    }

    this._imageEmpty.style.display = 'none';
    this._imageEl.classList.add('bb-is-visible');

    const idx = Math.min(this._activeImageIndex, allImages.length - 1);
    const img = allImages[idx];

    if (img && this._imageEl.src !== img.src) {
      this._imageEl.src   = img.src;
      this._imageEl.alt   = img.alt;
    }
  }

  /* ---- Render: Thumbnails ---- */

  _renderThumbnails() {
    if (!this._thumbnails) return;

    const allImages = this._getAllImages();

    if (allImages.length <= 1) {
      this._thumbnails.innerHTML = '';
      return;
    }

    const html = allImages.map((img, i) => `
      <img
        class="bb-thumbnail${i === this._activeImageIndex ? ' bb-thumbnail--active' : ''}"
        src="${img.src}"
        alt="${this._escHtml(img.alt)}"
        width="48"
        height="48"
        loading="lazy"
        data-action="show-image"
        data-index="${i}"
        role="listitem"
        tabindex="0"
      >
    `).join('');

    this._thumbnails.innerHTML = html;
  }

  /* ---- Render: Progress dots ---- */

  _updateProgressDots() {
    const towelDot  = this.querySelector('[data-progress="towel"]');
    const clipDot   = this.querySelector('[data-progress="clip"]');
    const extrasDot = this.querySelector('[data-progress="extras"]');

    if (towelDot) {
      towelDot.classList.toggle('bb-progress__dot--done', this._state.towels.length > 0);
      towelDot.classList.toggle('bb-progress__dot--active', this._state.towels.length === 0);
    }
    if (clipDot) {
      clipDot.classList.toggle('bb-progress__dot--done', this._state.clips.length > 0);
      clipDot.classList.toggle('bb-progress__dot--active',
        this._state.towels.length > 0 && this._state.clips.length === 0);
    }
    if (extrasDot) {
      const hasExtras = this._state.bottle > 0;
      extrasDot.classList.toggle('bb-progress__dot--done', hasExtras);
      extrasDot.classList.toggle('bb-progress__dot--active',
        this._state.clips.length > 0 && !hasExtras);
    }
  }

  /* ---- Render: Button enable/disable ---- */

  _updateButtonState(pricing) {
    if (!this._addToCart) return;

    const hasItems = pricing.totalItems > 0;
    this._addToCart.disabled = !hasItems;
    this._addToCart.setAttribute('aria-disabled', String(!hasItems));

    // Show express checkout when bundle has items; prefer Apple Pay over Shop Pay
    if (this._expressCheckout) {
      this._expressCheckout.style.display = hasItems ? '' : 'none';
      const applePayAvailable = window.ApplePaySession && ApplePaySession.canMakePayments();
      const shopPayBtn = this._expressCheckout.querySelector('.bb-shop-pay-btn');
      if (shopPayBtn) shopPayBtn.style.display = applePayAvailable ? 'none' : '';
    }
  }

  /* ---- Render: Qty pill active state ---- */

  _updateQtyDisplays() {
    const sid = this._sectionId;
    // Clip (pill + button combo)
    const clipContainer = document.getElementById(`ClipQty-${sid}`);
    if (clipContainer) {
      clipContainer.querySelectorAll('.bb-qty-pill').forEach((pill) => {
        const active = parseInt(pill.dataset.qty, 10) === this._state.clipQty;
        pill.classList.toggle('bb-qty-pill--active', active);
        pill.setAttribute('aria-pressed', String(active));
      });
    }
    // Bottle (pill buttons)
    const bottleContainer = document.getElementById(`BottleQty-${sid}`);
    if (bottleContainer) {
      bottleContainer.querySelectorAll('.bb-qty-pill').forEach((pill) => {
        const active = parseInt(pill.dataset.qty, 10) === this._state.bottle;
        pill.classList.toggle('bb-qty-pill--active', active);
        pill.setAttribute('aria-pressed', String(active));
      });
    }
    // Electrolytes (pill buttons)
    const electrolytesContainer = document.getElementById(`ElectrolytesQty-${sid}`);
    if (electrolytesContainer) {
      electrolytesContainer.querySelectorAll('.bb-qty-pill').forEach((pill) => {
        const active = parseInt(pill.dataset.qty, 10) === this._state.electrolytes;
        pill.classList.toggle('bb-qty-pill--active', active);
        pill.setAttribute('aria-pressed', String(active));
      });
    }
  }

  /* ---- Render: Towel swatch selected states ---- */

  _updateTowelSwatchSelectedStates() {
    if (!this._towelGrid) return;
    // Show count badges on swatches that are selected
    const selectedCounts = {};
    this._state.towels.forEach((t) => {
      selectedCounts[t.variantId] = (selectedCounts[t.variantId] || 0) + 1;
    });
    this._towelGrid.querySelectorAll('.bb-swatch').forEach((swatch) => {
      const vid = parseInt(swatch.dataset.variantId, 10);
      const count = selectedCounts[vid] || 0;
      swatch.classList.toggle('bb-swatch--selected', count > 0);

      // Update add button label with count
      const addBtn = swatch.querySelector('.bb-swatch__add');
      if (addBtn) {
        addBtn.textContent = count > 0 ? `+${count}` : '+';
      }
    });
  }

  /* ---- Mobile drawer ---- */

  _toggleMobileDrawer() {
    if (!this._mobileDrawer) return;
    const isOpen = this._mobileDrawer.classList.toggle('bb-mobile-drawer--open');
    document.body.classList.toggle('bb-drawer-open', isOpen);
    const btn = this._mobileDrawer.querySelector('.bb-mobile-drawer__toggle');
    if (btn) btn.setAttribute('aria-expanded', String(isOpen));
  }

  _toggleDesktopSummary() {
    if (!this._summaryPanel) return;
    const isOpen = this._summaryPanel.classList.toggle('bb-summary--open');
    const btn = this._summaryPanel.querySelector('.bb-summary__toggle');
    if (btn) btn.setAttribute('aria-expanded', String(isOpen));
  }

  _renderMobileDrawer() {
    if (!this._mobileDrawerItems || !this._mobileDrawerCount) return;

    const pricing = this.getCurrentPricing();
    this._mobileDrawerCount.textContent = pricing.totalItems;

    // Hide drawer when no items and clean up open state
    if (this._mobileDrawer) {
      if (pricing.totalItems === 0) {
        this._mobileDrawer.style.display = 'none';
        this._mobileDrawer.classList.remove('bb-mobile-drawer--open');
        document.body.classList.remove('bb-drawer-open');
        const btn = this._mobileDrawer.querySelector('.bb-mobile-drawer__toggle');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      } else {
        this._mobileDrawer.style.display = '';
      }
    }

    const items = [];

    this._state.towels.forEach((towel, i) => {
      const imgHtml = towel.swatch
        ? `<img class="bb-summary-item__swatch" src="${towel.swatch}" alt="" width="28" height="28">`
        : '';
      const linkHtml = towel.handle
        ? `<a class="bb-summary-item__link" href="/products/${this._escHtml(towel.handle)}">See product</a>`
        : '';
      items.push(`
        <div class="bb-summary-item">
          ${imgHtml}
          <div class="bb-summary-item__details">
            <span class="bb-summary-item__name">${this._escHtml(towel.title)}</span>
            ${linkHtml}
          </div>
          <button class="bb-summary-item__remove" type="button" data-action="remove-towel" data-index="${i}" aria-label="Remove ${this._escHtml(towel.title)}">&times;</button>
        </div>
      `);
    });

    if (this._state.clipQty > 0) {
      const clipImg = this._products && this._products.clip && this._products.clip.image
        ? `<img class="bb-summary-item__swatch" src="${this._products.clip.image}" alt="" width="28" height="28">`
        : '';
      items.push(`
        <div class="bb-summary-item">
          ${clipImg}
          <div class="bb-summary-item__details">
            <span class="bb-summary-item__name">XL Clip</span>
            <a class="bb-summary-item__link" href="/products/xl-clip">See product</a>
          </div>
          <span class="bb-summary-item__qty">&times;${this._state.clipQty}</span>
          <button class="bb-summary-item__remove" type="button" data-action="set-clip" data-qty="0" aria-label="Remove clips">&times;</button>
        </div>
      `);
    }

    if (this._state.bottle > 0) {
      const bottleImg = this._products && this._products.bottle && this._products.bottle.image
        ? `<img class="bb-summary-item__swatch" src="${this._products.bottle.image}" alt="" width="28" height="28">`
        : '';
      items.push(`
        <div class="bb-summary-item">
          ${bottleImg}
          <div class="bb-summary-item__details">
            <span class="bb-summary-item__name">Water Bottle</span>
            <a class="bb-summary-item__link" href="/products/insulated-water-bottle">See product</a>
          </div>
          <span class="bb-summary-item__qty">&times;${this._state.bottle}</span>
          <button class="bb-summary-item__remove" type="button" data-action="set-bottle" data-qty="0" aria-label="Remove bottle">&times;</button>
        </div>
      `);
    }

    if (this._state.electrolytes > 0) {
      const electrolytesImg = this._products && this._products.electrolytes && this._products.electrolytes.image
        ? `<img class="bb-summary-item__swatch" src="${this._products.electrolytes.image}" alt="" width="28" height="28">`
        : '';
      items.push(`
        <div class="bb-summary-item">
          ${electrolytesImg}
          <div class="bb-summary-item__details">
            <span class="bb-summary-item__name">Reload Electrolytes</span>
            <a class="bb-summary-item__link" href="/products/electrolyte-sachets">See product</a>
          </div>
          <span class="bb-summary-item__qty">&times;${this._state.electrolytes}</span>
          <button class="bb-summary-item__remove" type="button" data-action="set-electrolytes" data-qty="0" aria-label="Remove electrolytes">&times;</button>
        </div>
      `);
    }

    this._mobileDrawerItems.innerHTML = items.join('');

    const clearWrap = document.getElementById(`BundleDrawerClear-${this._sectionId}`);
    if (clearWrap) {
      if (items.length > 0) {
        clearWrap.innerHTML = `<button class="bb-clear-bundle bb-clear-bundle--drawer" type="button" data-action="clear-bundle">Clear Bundle</button>`;
      } else {
        clearWrap.innerHTML = '';
      }
    }
  }

  /* ---- Image navigation ---- */

  _showImageAtIndex(index) {
    const allImages = this._getAllImages();
    if (index >= 0 && index < allImages.length) {
      this._activeImageIndex = index;
      this._renderProductImage();
      this._renderThumbnails();
    }
  }

  /**
   * Build ordered list of images from current selections.
   * @returns {Array<{ src, alt }>}
   */
  _getAllImages() {
    const images = [];

    // Towels first
    this._state.towels.forEach((towel) => {
      if (towel.image) {
        images.push({ src: towel.image, alt: towel.title });
      }
    });

    // Bottle
    if (this._state.bottle > 0 && this._products && this._products.bottle && this._products.bottle.image) {
      images.push({ src: this._products.bottle.image, alt: 'Water Bottle' });
    }

    return images;
  }

  /* ---- Cart integration ---- */

  /**
   * Fire GA4 add_to_cart (via GTM dataLayer) + guarded Meta AddToCart for the
   * built bundle. Deliberately mirrors the standard PDP path in product-form.js
   * (identical event name + ecommerce shape) so the existing GTM ecommerce tags
   * process it with NO GTM changes. The fbq call mirrors the existing guarded
   * ViewContent in main-bundle-builder.liquid. Wrapped so analytics can never
   * break the cart add / checkout redirect.
   */
  _pushAddToCartEvent() {
    try {
      const pricing = this.getCurrentPricing();
      const utm = (window.dansuAnalytics && window.dansuAnalytics.getUtmParams)
        ? window.dansuAnalytics.getUtmParams() : {};
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';

      const items = [];
      const contentIds = [];

      (this._state.towels || []).forEach((t) => {
        items.push({
          item_id: String(t.variantId),
          item_name: t.title || 'Rave Towel',
          item_brand: 'Dansu',
          item_category: 'Rave Pack',
          price: BundlePricing.UNIT_PRICES.towel / 100,
          quantity: 1,
        });
        contentIds.push(String(t.variantId));
      });

      if (this._state.clipQty > 0 && this._products && this._products.clip) {
        const clipVariant = this._products.clip.variants.find((v) => v.available);
        if (clipVariant) {
          items.push({
            item_id: String(clipVariant.id),
            item_name: 'XL Clip',
            item_brand: 'Dansu',
            item_category: 'Rave Pack',
            price: BundlePricing.UNIT_PRICES.clip / 100,
            quantity: this._state.clipQty,
          });
          contentIds.push(String(clipVariant.id));
        }
      }

      if (this._state.bottle > 0 && this._products && this._products.bottle) {
        items.push({
          item_id: String(this._products.bottle.variantId),
          item_name: 'Insulated Water Bottle',
          item_brand: 'Dansu',
          item_category: 'Rave Pack',
          price: BundlePricing.UNIT_PRICES.bottle / 100,
          quantity: this._state.bottle,
        });
        contentIds.push(String(this._products.bottle.variantId));
      }

      if (this._state.electrolytes > 0 && this._products && this._products.electrolytes) {
        items.push({
          item_id: String(this._products.electrolytes.variantId),
          item_name: 'Reload Electrolytes',
          item_brand: 'Dansu',
          item_category: 'Rave Pack',
          price: BundlePricing.UNIT_PRICES.electrolytes / 100,
          quantity: this._state.electrolytes,
        });
        contentIds.push(String(this._products.electrolytes.variantId));
      }

      if (items.length === 0) return;

      const value = pricing.bundlePrice / 100;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        utm_source: utm.utm_source || '',
        utm_medium: utm.utm_medium || '',
        utm_campaign: utm.utm_campaign || '',
        utm_content: utm.utm_content || '',
        utm_term: utm.utm_term || '',
        ecommerce: {
          currency: currency,
          value: value,
          coupon: pricing.discountCode || '',
          items: items,
        },
      });

      if (typeof fbq === 'function') {
        fbq('track', 'AddToCart', {
          content_type: 'product',
          content_ids: contentIds,
          currency: currency,
          value: value,
        });
      }
    } catch (e) {
      /* analytics must never break the cart add / checkout redirect */
    }
  }

  async _handleAddToCart() {
    const pricing = this.getCurrentPricing();
    if (pricing.totalItems === 0) return;

    // Build items array for Shopify Cart API
    const items = [];
    const bundleId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);

    this._state.towels.forEach((t) => {
      items.push({
        id: t.variantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
        },
      });
    });

    if (this._state.clipQty > 0 && this._products && this._products.clip) {
      const clipVariant = this._products.clip.variants.find(v => v.available);
      if (clipVariant) {
        items.push({
          id: clipVariant.id,
          quantity: this._state.clipQty,
          properties: {
            _bundle_id: bundleId,
          },
        });
      }
    }

    if (this._state.bottle > 0 && this._products && this._products.bottle) {
      items.push({
        id: this._products.bottle.variantId,
        quantity: this._state.bottle,
        properties: {
          _bundle_id: bundleId,
        },
      });
    }

    if (this._state.electrolytes > 0 && this._products && this._products.electrolytes) {
      items.push({
        id: this._products.electrolytes.variantId,
        quantity: this._state.electrolytes,
        properties: {
          _bundle_id: bundleId,
        },
      });
    }

    if (items.length === 0) return;

    // Loading state
    this._setCartLoading(true);

    try {
      // Clear cart first so re-buying doesn't duplicate items
      await fetch('/cart/clear.js', { method: 'POST' });

      // Batch add to cart
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!addRes.ok) {
        const errData = await addRes.json().catch(() => ({}));
        throw new Error(errData.description || 'Cart add failed');
      }

      // Tracking: cart add succeeded — fire add_to_cart before the redirect
      this._pushAddToCartEvent();

      // Always go straight to checkout — bundle is already built, no need for cart drawer
      // Rave Pack (RAVE_PACK_CODE) carts go through plain checkout — the storewide
      // automatic discount now applies without the ?discount= code. Other bundle
      // codes (BUNDLE10-25) still redirect with their code, unchanged.
      const checkoutUrl = (pricing.discountCode && pricing.discountCode !== BundlePricing.RAVE_PACK_CODE)
        ? `/checkout?discount=${encodeURIComponent(pricing.discountCode)}`
        : '/checkout';
      window.location.href = checkoutUrl;

    } catch (err) {
      console.error('[BundleBuilder] Cart error:', err);
      this._announce('Sorry, there was an error adding items to your cart. Please try again.');
    } finally {
      this._setCartLoading(false);
    }
  }

  async _handleClearCart() {
    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      this._announce('Basket cleared.');
      // Update cart count in theme if possible
      if (typeof publish === 'function') publish('cart-update', { source: 'bundle-builder' });
    } catch (err) {
      console.error('[BundleBuilder] Clear cart error:', err);
    }
  }

  async _handleBuyNow() {
    const pricing = this.getCurrentPricing();
    if (pricing.totalItems === 0) return;

    const items = [];
    const bundleId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);

    this._state.towels.forEach((t) => {
      items.push({ id: t.variantId, quantity: 1, properties: { _bundle_id: bundleId } });
    });

    if (this._state.clipQty > 0 && this._products && this._products.clip) {
      const clipVariant = this._products.clip.variants.find(v => v.available);
      if (clipVariant) items.push({ id: clipVariant.id, quantity: this._state.clipQty, properties: { _bundle_id: bundleId } });
    }

    if (this._state.bottle > 0 && this._products && this._products.bottle) {
      items.push({ id: this._products.bottle.variantId, quantity: this._state.bottle, properties: { _bundle_id: bundleId } });
    }

    if (this._state.electrolytes > 0 && this._products && this._products.electrolytes) {
      items.push({ id: this._products.electrolytes.variantId, quantity: this._state.electrolytes, properties: { _bundle_id: bundleId } });
    }

    if (items.length === 0) return;

    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Cart add failed');

      // Tracking: cart add succeeded — fire add_to_cart before the redirect
      this._pushAddToCartEvent();

      // Rave Pack (RAVE_PACK_CODE) carts go through plain checkout — the storewide
      // automatic discount now applies without the ?discount= code. Other bundle
      // codes (BUNDLE10-25) still redirect with their code, unchanged.
      const checkoutUrl = (pricing.discountCode && pricing.discountCode !== BundlePricing.RAVE_PACK_CODE)
        ? `/checkout?discount=${encodeURIComponent(pricing.discountCode)}`
        : '/checkout';
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('[BundleBuilder] Buy now error:', err);
      this._announce('Sorry, there was an error. Please try again.');
    }
  }

  _setCartLoading(loading) {
    if (!this._addToCart) return;
    this._addToCart.classList.toggle('bb-add-to-cart--loading', loading);
    this._addToCart.disabled = loading;
  }

  /* ---- Helpers ---- */

  _announce(message) {
    if (!this._liveRegion) return;
    this._liveRegion.textContent = '';
    requestAnimationFrame(() => {
      this._liveRegion.textContent = message;
    });
  }

  _escHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  _getColorStyle(colorSlug) {
    const colorMap = {
      black:  '#1a1a1a',
      sand:   '#c9b99a',
      forest: '#3a5a40',
      tan:    '#a0785a',
      rust:   '#b04d2a',
    };
    const bg = colorMap[colorSlug];
    return bg ? `background:${bg};` : '';
  }

  _flashSection(sectionId) {
    const header = this.querySelector(`#${sectionId} .bb-section__title`);
    if (header) {
      header.style.color = '#00e87b';
      setTimeout(() => { header.style.color = ''; }, 1000);
    }
  }
}

/* ============================================================
   C) Custom element registration
   ============================================================ */
if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}

